import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { notifyOk, notifyError } from "@/lib/notify";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Field } from "@/components/forms/fields";
import { Plus, Building2, ChevronRight, MapPin, UserCheck } from "lucide-react";

export const ONBOARDING_LABEL = {
  borrador: "Borrador",
  en_revision: "En revisión",
  validado: "Validado",
};
const ONBOARDING_CLASS = {
  borrador: "bg-slate-100 text-slate-600",
  en_revision: "bg-amber-50 text-amber-700",
  validado: "bg-emerald-50 text-emerald-700",
};

export function OnboardingBadge({ estado, testId }) {
  const e = estado || "borrador";
  return (
    <span data-testid={testId} className={`text-[11px] rounded-full px-2.5 py-1 font-medium ${ONBOARDING_CLASS[e]}`}>
      {ONBOARDING_LABEL[e] || e}
    </span>
  );
}

export default function Organizaciones() {
  const navigate = useNavigate();
  const { isSupervisor } = useAuth();
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ nombre: "", tipo: "institucional" });
  const [saving, setSaving] = useState(false);
  const [incluirInactivas, setIncluirInactivas] = useState(false);

  useEffect(() => {
    if (!isSupervisor) {
      notifyError("Sólo un supervisor de Galarya administra organizaciones.");
      navigate("/consola-v3", { replace: true });
    }
  }, [isSupervisor, navigate]);

  const load = () => {
    if (!isSupervisor) return;
    setLoading(true);
    api.get("/organizations").then((r) => setOrgs(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  // "Incluir inactivas" es un filtro de PRESENTACIÓN: no cambia estados ni autorización.
  const visibles = incluirInactivas ? orgs : orgs.filter((o) => o.estado !== "Inactivo");

  const crear = async () => {
    setSaving(true);
    try {
      const r = await api.post("/organizations", f);
      notifyOk("Organización creada. Ya puedes crear sitios y operar.");
      setOpen(false);
      setF({ nombre: "", tipo: "institucional" });
      navigate(`/organizaciones/${r.data.id}`);
    } catch (e) {
      notifyError(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    // El puente de tema nocturno (index.css · `.tema-noche-auto`) faltaba en esta página: su
    // lista `bg-white` seguía CLARA de noche mientras el nombre (`text-[#012472]` + regla de
    // h1-h4) ya se aclaraba a near-white → nombres blancos sobre blanco. Misma marca y mismos
    // tokens que Sitios/Bitácora; sin estados de tema propios ni CSS nuevo.
    <div className="tema-noche-auto space-y-6" data-testid="organizaciones-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-[#012472]">Organizaciones</h1>
          <p className="text-sm text-slate-500 mt-1">
            Expediente administrativo y provisioning institucional. El estado del expediente NO
            afecta el monitoreo.
          </p>
        </div>
        <Button data-testid="add-organization-button" onClick={() => setOpen(true)}
          className="rounded-xl bg-[#01CBE3] hover:bg-[#01B5CB] text-white">
          <Plus strokeWidth={1.75} className="h-4 w-4 mr-1.5" /> Nueva organización
        </Button>
      </div>

      <label className="inline-flex items-center gap-2 text-xs text-slate-500 cursor-pointer">
        <input type="checkbox" checked={incluirInactivas} data-testid="org-incluir-inactivas"
          onChange={(e) => setIncluirInactivas(e.target.checked)}
          className="h-4 w-4 rounded border-slate-300 accent-[#012472]" />
        Incluir inactivas
      </label>

      {loading ? (
        <p className="text-slate-400">Cargando organizaciones…</p>
      ) : visibles.length === 0 ? (
        <p data-testid="organizaciones-empty" className="text-slate-400">Aún no hay organizaciones.</p>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)] divide-y divide-gray-100 overflow-hidden">
          {visibles.map((o) => (
            <button key={o.id} data-testid={`organization-row-${o.id}`}
              onClick={() => navigate(`/organizaciones/${o.id}`)}
              className="w-full text-left px-5 py-4 flex flex-wrap sm:flex-nowrap items-start sm:items-center gap-x-4 gap-y-3 hover:bg-slate-50/70 transition-colors group">
              <div className="h-10 w-10 rounded-xl bg-[#012472]/5 flex items-center justify-center shrink-0">
                <Building2 strokeWidth={1.5} className="h-5 w-5 text-[#012472]" />
              </div>
              <div className="min-w-0 flex-1 basis-[60%]">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 title={o.nombre} className="text-base font-medium text-[#012472] max-w-full break-words [overflow-wrap:anywhere] line-clamp-2">{o.nombre}</h3>
                  <span className="text-[10px] uppercase tracking-wide rounded-full bg-[#012472]/5 text-[#012472] px-2 py-0.5">
                    {o.tipo === "institucional" ? "Institucional" : "Residencial"}
                  </span>
                  {o.estado === "Inactivo" && (
                    <span className="text-[10px] rounded-full bg-slate-100 text-slate-500 px-2 py-0.5">Inactiva</span>
                  )}
                  {o.acceso_con_expediente_pendiente && (
                    <span data-testid={`org-badge-pendiente-${o.id}`}
                      className="text-[10px] font-semibold uppercase tracking-wide leading-tight rounded-full bg-amber-50 text-amber-700 px-2 py-0.5 max-w-full">
                      Acceso activo · Expediente pendiente
                    </span>
                  )}
                </div>
                <p className="text-xs text-slate-400 mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                  <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {o.num_sitios} sitio(s)</span>
                  <span className="flex items-center gap-1"><UserCheck className="h-3.5 w-3.5" /> {o.num_usuarios_externos} usuario(s) institucional(es)</span>
                </p>
              </div>
              <div className="flex items-center gap-2 w-full sm:w-auto justify-between sm:justify-end shrink-0">
                <OnboardingBadge estado={o.onboarding_status} testId={`org-onboarding-${o.id}`} />
                <ChevronRight strokeWidth={1.5} className="h-5 w-5 text-slate-300 group-hover:text-[#01CBE3] shrink-0" />
              </div>
            </button>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="w-[calc(100%-2rem)] max-w-md rounded-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#012472]">Nueva organización</DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Alta inicial: sólo el nombre y el tipo. Al guardar se abre el expediente de la
              organización, donde el resto de la información se completa por pestañas cuando la
              tengas. Con esto la organización ya es usable: puedes crear sitios, dar de alta
              residentes y operar sin completar el expediente.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Field label="Nombre (comercial o de display)">
              <Input data-testid="org-nombre-input" className="rounded-xl" value={f.nombre}
                onChange={(e) => setF((p) => ({ ...p, nombre: e.target.value }))} />
            </Field>
            <Field label="Tipo">
              <Select value={f.tipo} onValueChange={(v) => setF((p) => ({ ...p, tipo: v }))}>
                <SelectTrigger className="rounded-xl" data-testid="org-tipo-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="institucional">Institucional</SelectItem>
                  <SelectItem value="residencial">Residencial</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button data-testid="org-guardar-button" disabled={saving || !f.nombre.trim()}
              className="rounded-xl bg-[#01CBE3] hover:bg-[#01B5CB] text-white" onClick={crear}>
              Guardar y continuar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
